'use client';

import React from 'react';
import {
  UploadIcon,
  PlusIcon,
  TrashIcon,
  DownloadIcon,
  Share1Icon,
  CopyIcon,
  ScissorsIcon,
  ArchiveIcon,
  LockClosedIcon,
  LockOpen1Icon,
  ViewGridIcon,
  ListBulletIcon,
} from '@radix-ui/react-icons';

import { Box, BoxProps } from '../box';
import { Stack } from '../stack';
import { Button } from '../button';
import { IconButton } from '../icon-button';
import { Separator } from '../separator';
import { Badge } from '../badge';
import { Text } from '../text';

export interface FileActionsProps extends BoxProps {
  /** Number of selected files */
  selectedCount?: number;
  /** Total number of files */
  totalCount?: number;
  /** Current view mode */
  viewMode?: 'list' | 'grid';
  /** Available actions */
  actions?: Array<
    | 'upload'
    | 'create'
    | 'delete'
    | 'download'
    | 'share'
    | 'copy'
    | 'move'
    | 'archive'
    | 'lock'
    | 'unlock'
  >;
  /** Whether actions are disabled */
  disabled?: boolean;
  /** Action callbacks */
  onUpload?: () => void;
  onCreateFolder?: () => void;
  onDelete?: () => void;
  onDownload?: () => void;
  onShare?: () => void;
  onCopy?: () => void;
  onMove?: () => void;
  onArchive?: () => void;
  onLock?: () => void;
  onUnlock?: () => void;
  onViewModeChange?: (mode: 'list' | 'grid') => void;
  /** Show selection info */
  showSelectionInfo?: boolean;
  /** Compact mode */
  compact?: boolean;
}

/**
 * FileActions component - toolbar for file management actions
 * Provides common file operations with contextual enabling/disabling
 */
export const FileActions: React.FC<FileActionsProps> = ({
  selectedCount = 0,
  totalCount = 0,
  viewMode = 'list',
  actions = ['upload', 'create', 'delete', 'download', 'share', 'copy', 'move'],
  disabled = false,
  onUpload,
  onCreateFolder,
  onDelete,
  onDownload,
  onShare,
  onCopy,
  onMove,
  onArchive,
  onLock,
  onUnlock,
  onViewModeChange,
  showSelectionInfo = true,
  compact = false,
  ...boxProps
}) => {
  const hasSelection = selectedCount > 0;
  const multipleSelected = selectedCount > 1;

  // ActionButton removed - handling IconButton vs Button individually
  const buttonSize = compact ? 'sm' : 'md';

  const renderAction = (action: string) => {
    switch (action) {
      case 'upload':
        return compact ? (
          <IconButton
            key="upload"
            size={buttonSize}
            variant="primary"
            onClick={onUpload}
            disabled={disabled}
            icon={<UploadIcon />}
            aria-label="Upload files"
            title="Upload files"
          />
        ) : (
          <Button
            key="upload"
            size={buttonSize}
            variant="primary"
            onClick={onUpload}
            disabled={disabled}
            leftIcon={<UploadIcon />}
            aria-label="Upload files"
            title="Upload files"
          >
            Upload
          </Button>
        );

      case 'create':
        return compact ? (
          <IconButton
            key="create"
            size={buttonSize}
            variant="outline"
            onClick={onCreateFolder}
            disabled={disabled}
            icon={<PlusIcon />}
            aria-label="New folder"
            title="New folder"
          />
        ) : (
          <Button
            key="create"
            size={buttonSize}
            variant="outline"
            onClick={onCreateFolder}
            disabled={disabled}
            leftIcon={<PlusIcon />}
            aria-label="New folder"
            title="New folder"
          >
            New Folder
          </Button>
        );

      case 'delete':
        return compact ? (
          <IconButton
            key="delete"
            size={buttonSize}
            variant="ghost"
            onClick={onDelete}
            disabled={disabled || !hasSelection}
            icon={<TrashIcon />}
            aria-label="Delete"
            title={`Delete ${selectedCount} item${multipleSelected ? 's' : ''}`}
          />
        ) : (
          <Button
            key="delete"
            size={buttonSize}
            variant="ghost"
            onClick={onDelete}
            disabled={disabled || !hasSelection}
            leftIcon={<TrashIcon />}
            aria-label="Delete"
            title={`Delete ${selectedCount} item${multipleSelected ? 's' : ''}`}
          >
            Delete
          </Button>
        );

      case 'download':
        return compact ? (
          <IconButton
            key="download"
            size={buttonSize}
            variant="ghost"
            onClick={onDownload}
            disabled={disabled || !hasSelection}
            icon={<DownloadIcon />}
            aria-label="Download"
            title={`Download ${selectedCount} item${multipleSelected ? 's' : ''}`}
          />
        ) : (
          <Button
            key="download"
            size={buttonSize}
            variant="ghost"
            onClick={onDownload}
            disabled={disabled || !hasSelection}
            leftIcon={<DownloadIcon />}
            aria-label="Download"
            title={`Download ${selectedCount} item${multipleSelected ? 's' : ''}`}
          >
            Download
          </Button>
        );

      case 'share':
        return compact ? (
          <IconButton
            key="share"
            size={buttonSize}
            variant="ghost"
            onClick={onShare}
            disabled={disabled || !hasSelection}
            icon={<Share1Icon />}
            aria-label="Share"
            title={`Share ${selectedCount} item${multipleSelected ? 's' : ''}`}
          />
        ) : (
          <Button
            key="share"
            size={buttonSize}
            variant="ghost"
            onClick={onShare}
            disabled={disabled || !hasSelection}
            leftIcon={<Share1Icon />}
            aria-label="Share"
            title={`Share ${selectedCount} item${multipleSelected ? 's' : ''}`}
          >
            Share
          </Button>
        );

      case 'copy':
        return compact ? (
          <IconButton
            key="copy"
            size={buttonSize}
            variant="ghost"
            onClick={onCopy}
            disabled={disabled || !hasSelection}
            icon={<CopyIcon />}
            aria-label="Copy"
            title={`Copy ${selectedCount} item${multipleSelected ? 's' : ''}`}
          />
        ) : (
          <Button
            key="copy"
            size={buttonSize}
            variant="ghost"
            onClick={onCopy}
            disabled={disabled || !hasSelection}
            leftIcon={<CopyIcon />}
            aria-label="Copy"
            title={`Copy ${selectedCount} item${multipleSelected ? 's' : ''}`}
          >
            Copy
          </Button>
        );

      case 'move':
        return compact ? (
          <IconButton
            key="move"
            size={buttonSize}
            variant="ghost"
            onClick={onMove}
            disabled={disabled || !hasSelection}
            icon={<ScissorsIcon />}
            aria-label="Move"
            title={`Move ${selectedCount} item${multipleSelected ? 's' : ''}`}
          />
        ) : (
          <Button
            key="move"
            size={buttonSize}
            variant="ghost"
            onClick={onMove}
            disabled={disabled || !hasSelection}
            leftIcon={<ScissorsIcon />}
            aria-label="Move"
            title={`Move ${selectedCount} item${multipleSelected ? 's' : ''}`}
          >
            Move
          </Button>
        );

      case 'archive':
        return compact ? (
          <IconButton
            key="archive"
            size={buttonSize}
            variant="ghost"
            onClick={onArchive}
            disabled={disabled || !hasSelection}
            icon={<ArchiveIcon />}
            aria-label="Archive"
            title={`Archive ${selectedCount} item${multipleSelected ? 's' : ''}`}
          />
        ) : (
          <Button
            key="archive"
            size={buttonSize}
            variant="ghost"
            onClick={onArchive}
            disabled={disabled || !hasSelection}
            leftIcon={<ArchiveIcon />}
            aria-label="Archive"
            title={`Archive ${selectedCount} item${multipleSelected ? 's' : ''}`}
          >
            Archive
          </Button>
        );

      case 'lock':
        return compact ? (
          <IconButton
            key="lock"
            size={buttonSize}
            variant="ghost"
            onClick={onLock}
            disabled={disabled || !hasSelection}
            icon={<LockClosedIcon />}
            aria-label="Lock"
            title={`Lock ${selectedCount} item${multipleSelected ? 's' : ''}`}
          />
        ) : (
          <Button
            key="lock"
            size={buttonSize}
            variant="ghost"
            onClick={onLock}
            disabled={disabled || !hasSelection}
            leftIcon={<LockClosedIcon />}
            aria-label="Lock"
            title={`Lock ${selectedCount} item${multipleSelected ? 's' : ''}`}
          >
            Lock
          </Button>
        );

      case 'unlock':
        return compact ? (
          <IconButton
            key="unlock"
            size={buttonSize}
            variant="ghost"
            onClick={onUnlock}
            disabled={disabled || !hasSelection}
            icon={<LockOpen1Icon />}
            aria-label="Unlock"
            title={`Unlock ${selectedCount} item${multipleSelected ? 's' : ''}`}
          />
        ) : (
          <Button
            key="unlock"
            size={buttonSize}
            variant="ghost"
            onClick={onUnlock}
            disabled={disabled || !hasSelection}
            leftIcon={<LockOpen1Icon />}
            aria-label="Unlock"
            title={`Unlock ${selectedCount} item${multipleSelected ? 's' : ''}`}
          >
            Unlock
          </Button>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      padding={compact ? 2 : 3}
      backgroundColor="bg.surface"
      borderRadius="lg"
      border="1px solid"
      borderColor="border.muted"
      {...boxProps}
    >
      <Stack direction="row" gap={2} alignItems="center">
        {/* Primary actions */}
        {actions.includes('upload') && renderAction('upload')}
        {actions.includes('create') && renderAction('create')}

        {/* Separator between primary and secondary actions */}
        {(actions.includes('upload') || actions.includes('create')) &&
          actions.some((a) =>
            ['delete', 'download', 'share', 'copy', 'move', 'archive', 'lock', 'unlock'].includes(a)
          ) && <Separator orientation="vertical" />}

        {/* Secondary actions */}
        {actions.includes('download') && renderAction('download')}
        {actions.includes('share') && renderAction('share')}
        {actions.includes('copy') && renderAction('copy')}
        {actions.includes('move') && renderAction('move')}
        {actions.includes('archive') && renderAction('archive')}
        {actions.includes('lock') && renderAction('lock')}
        {actions.includes('unlock') && renderAction('unlock')}

        {/* Delete action (separated) */}
        {actions.includes('delete') && (
          <>
            <Separator orientation="vertical" />
            {renderAction('delete')}
          </>
        )}
      </Stack>

      <Stack direction="row" gap={3} alignItems="center">
        {/* Selection info */}
        {showSelectionInfo && hasSelection && (
          <>
            <Badge size={compact ? 'sm' : 'md'}>{selectedCount} selected</Badge>
            {!compact && totalCount > 0 && (
              <Text fontSize="sm" color="fg.muted">
                of {totalCount}
              </Text>
            )}
          </>
        )}

        {/* View mode toggle */}
        {onViewModeChange && (
          <>
            <Separator orientation="vertical" />
            <Stack direction="row" gap={1}>
              <IconButton
                size={buttonSize}
                variant={viewMode === 'list' ? 'primary' : 'ghost'}
                onClick={() => onViewModeChange('list')}
                aria-label="List view"
                title="List view"
                icon={<ListBulletIcon />}
              />
              <IconButton
                size={buttonSize}
                variant={viewMode === 'grid' ? 'primary' : 'ghost'}
                onClick={() => onViewModeChange('grid')}
                aria-label="Grid view"
                title="Grid view"
                icon={<ViewGridIcon />}
              />
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default FileActions;
